import { Controller } from '@hotwired/stimulus';
import { scrollToCalendar } from '../mobile_scroll';

// Controller for calendar date/time selection
// Used in booking process step 2 (date/time selection)
// Receives availability data from server and renders time slots dynamically
export default class extends Controller {
  static targets = ['timeSlotsWrapper', 'timeSlots', 'timeSlotsList', 'dateInput'];
  static values = {
    availability: { type: Array, default: [] },
    // Single-page flow: submit the form as soon as a time is picked (→ upsells). The multi-step
    // code path leaves this false and advances via the explicit "Weiter" button instead.
    autoSubmit: { type: Boolean, default: false },
    // Localized by the server: ES modules cannot reach the Rails translations.
    noTimesAvailableText: String
  };

  connect() {
    this.selectedDate = null;
    this.availabilityMap = this.buildAvailabilityMap();

    // Event delegation for dynamically added time slot radio buttons
    this.element.addEventListener('change', (e) => this.handleTimeChange(e));

    // Mobile: the calendar has just been revealed — anchor it in view.
    scrollToCalendar();
  }

  handleTimeChange(event) {
    const radio = event.target;
    if (radio.type === 'radio' && radio.name.includes('[time]')) {
      this.selectTime({ currentTarget: radio });
    }
  }

  // Build a map of date -> times for quick lookup
  buildAvailabilityMap() {
    const map = {};
    this.availabilityValue.forEach((day) => {
      map[day.date] = day.times;
    });
    return map;
  }

  selectDate(event) {
    const button = event.currentTarget;
    const date = button.dataset.date;

    if (!date || button.disabled) return;

    // Deselect all other dates
    this.element.querySelectorAll('.calendar__cell-button').forEach((btn) => {
      btn.setAttribute('aria-selected', 'false');
      btn.classList.remove('selected');
    });

    // Select this date
    button.setAttribute('aria-selected', 'true');
    button.classList.add('selected');
    this.selectedDate = date;

    // On mobile the month grid collapses into one horizontally scrollable day strip once a day is
    // picked (CSS keys off this class); desktop keeps the full grid alongside the time column.
    this.element.querySelector('.calendar')?.classList.add('calendar--day-selected');

    // Update hidden input
    if (this.hasDateInputTarget) {
      this.dateInputTarget.value = date;
    }

    // Only render time slots dynamically if we have availability data
    // Otherwise, the static time slots from the server are already rendered
    if (this.availabilityValue.length > 0) {
      this.renderTimeSlots(date);
    }

    // Show time slots panel
    if (this.hasTimeSlotsTarget) {
      this.timeSlotsTarget.classList.remove('hidden');
    }

    // Mobile: center the picked day in the compact strip, and anchor the calendar in view.
    this.scrollStripToSelected(button);
    scrollToCalendar();
  }

  // Mobile compact view: horizontally scroll the collapsed day strip so the selected day is centered.
  scrollStripToSelected(button) {
    if (!window.matchMedia('(max-width: 639px)').matches) return;

    requestAnimationFrame(() => {
      const strip = this.element.querySelector('.calendar__body');
      if (!strip || strip.scrollWidth <= strip.clientWidth) return;

      const stripRect = strip.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const delta = buttonRect.left + buttonRect.width / 2 - (stripRect.left + stripRect.width / 2);
      strip.scrollBy({ left: delta, behavior: 'smooth' });
    });
  }

  renderTimeSlots(date) {
    if (!this.hasTimeSlotsListTarget) return;

    const times = this.availabilityMap[date] || [];

    if (times.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'calendar__empty';
      empty.textContent = this.noTimesAvailableTextValue;
      this.timeSlotsListTarget.innerHTML = empty.outerHTML;
      return;
    }

    const html = times
      .map(
        (time) => `
      <label class="booking-time-slot">
        <input type="radio"
               name="form[time]"
               value="${time}"
               data-action="change->booking-calendar#selectTime">
        <span class="booking-time-slot__label">${time}</span>
      </label>
    `
      )
      .join('');

    this.timeSlotsListTarget.innerHTML = html;
  }

  selectTime(event) {
    const radio = event.currentTarget;
    const time = radio.value;

    // Combine date and time into starts_at
    if (this.selectedDate && time) {
      // Search in the whole form, not just controller element
      const form = this.element.closest('form');
      const startsAtInput =
        form?.querySelector('input[name*="starts_at"]') ||
        this.element.querySelector('input[name*="starts_at"]');

      if (startsAtInput) {
        startsAtInput.value = `${this.selectedDate} ${time}`;
      }

      // Single-page flow: picking a time is the final step — submit straight to upsells.
      if (this.autoSubmitValue && form) {
        form.requestSubmit();
      }
    }
  }
}
